<template>
  <div class="rounded-2xl border border-gray-200 bg-white p-6 h-full">
    <div class="flex flex-col gap-5 mb-6 sm:flex-row sm:justify-between">
      <div class="w-full">
        <h3 class="text-lg font-semibold text-gray-800">Opérations</h3>
        <p class="mt-1 text-gray-500 text-theme-sm">
          Statistiques détaillés de vos opérations
        </p>
      </div>

      <div class="relative">
        <div
          class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
        >
          <button
            v-for="option in options"
            :key="option.value"
            @click="selectPeriod(option)"
            :class="[
              selectedPeriod === option.value
                ? 'shadow-theme-xs text-gray-900  bg-white'
                : 'text-gray-500',
              'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs',
            ]"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="max-w-full overflow-auto custom-scrollbar">
      <div>
        <div class="-ml-4 min-w-[1000px] md:min-w-full pl-2 md:overflow-hidden">
          <VueApexCharts
            type="bar"
            height="350"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts'
import { onMounted, ref, watch } from 'vue'
import Commons from '@/shared/utils/commons'

const props = defineProps({
  statsData: {
    type: Object,
    default: () => ({ initiated: null, succeeded: null, failed: null })
  }
})

const options = [
  {
    value: 'year',
    label: 'Année',
    chartColumntWidth: '45%',
    periodMarks: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
  },
  {
    value: 'month',
    label: 'Mois',
    chartColumntWidth: '70%',
    periodMarks: Array.from({ length: 31 }, (_, i) => (i + 1).toString()), // Jours du mois 1 à 31
  },
  {
    value: 'week',
    label: 'Semaine',
    chartColumntWidth: '30%',
    periodMarks: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'], // Jours de la semaine
  },
]

const selectedPeriod = ref('week')
const oldStats = ref(null)

const series = ref([
  {
    name: 'Total opérations',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Réussi',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Echoué',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'En cours',
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
])

const chartOptions = ref({
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    markers: {
      shape: 'circle',
    },
  },
  colors: ['#FF7200', '#FFA200', '#FFC260', '#FFD2A0'],
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'bar',
    height: 350,
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  plotOptions: {
    bar: {
      columnWidth: '35%', // ou '30%' selon ton goût
      horizontal: false,
      borderRadius: 10,
      borderRadiusApplication: 'end', // 'around', 'end'
      borderRadiusWhenStacked: 'last', // 'all', 'last'
      dataLabels: {
        enabled: false,
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  markers: {
    size: 0,
  },
  labels: {
    show: false,
    position: 'top',
  },
  xaxis: {
    type: 'category',
    // type: 'datetime',
    categories: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
    fill: {
      opacity: 1,
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    tooltip: {
      enabled: false,
    },
  },

  tooltip: {
    y: {
      title: { formatter: () => '' },
      formatter: (val, { seriesIndex, dataPointIndex, w }) => {
        const dataset = w.config.series[seriesIndex]
        const count = dataset.counts?.[dataPointIndex] ?? 0
        const amount = dataset.amounts?.[dataPointIndex] ?? 0
        return `<div style="">
        <b>${series.value[seriesIndex]?.name}</b><br/>
        <span>Montant :</span> ${amount.toLocaleString()} FCFA<br/>
        <span>Nombre :</span> ${count} opération${count > 1 ? 's' : ''}
      </div>`
      },
    },
  },
})

function selectPeriod(option, initial) {
  if (!initial && selectedPeriod.value == option.value) return
  selectedPeriod.value = option.value
  oldStats.value = null

  // Réinitialise les données
  series.value = series.value.map(el => ({
    ...el,
    data: option.periodMarks.map(() => 60),
  }))

  // Met à jour les catégories de l'axe X
  chartOptions.value = {
    ...chartOptions.value,
    plotOptions: {
      ...chartOptions.value.plotOptions,
      bar: {
        ...chartOptions.value.plotOptions.bar,
        columnWidth: option.chartColumntWidth || '40%',
        // option.periodMarks.length > 20
        //   ? '60%'
        //   : option.periodMarks.length < 8
        //     ? '20%'
        //     : '35%',
      },
    },
    xaxis: { ...chartOptions.value.xaxis, categories: [...option.periodMarks] },
  }
}

function setStats(totalOperations, totalSuccess, totalFailed) {
  if (!totalOperations?.stats || !totalFailed?.stats || !totalSuccess?.stats)
    return
  if (
    Commons.deepEqual(oldStats.value, {
      totalOperations,
      totalSuccess,
      totalFailed,
    })
  )
    return
  oldStats.value = { totalOperations, totalSuccess, totalFailed }

  series.value = [
    {
      ...series.value[0],
      data: totalOperations.stats.map(el => el.count),
      counts: totalOperations.stats.map(el => el.count),
      amounts: totalOperations.stats.map(el => el.amount),
    },
    {
      ...series.value[1],
      data: totalSuccess.stats.map(el => el.count),
      counts: totalSuccess.stats.map(el => el.count),
      amounts: totalSuccess.stats.map(el => el.amount),
    },
    {
      ...series.value[2],
      data: totalFailed.stats.map(el => el.count),
      counts: totalFailed.stats.map(el => el.count),
      amounts: totalFailed.stats.map(el => el.amount),
    },
    {
      ...series.value[3],
      data: totalOperations.stats.map((initiated, i) => {
        // const successAmount = totalSuccess.stats[i]?.amount
        // const failedAmount = totalFailed.stats[i]?.amount
        const successValue = totalSuccess.stats[i]?.count
        const failedValue = totalFailed.stats[i]?.count
        return Math.max(0, initiated.count - successValue - failedValue)
      }),
      counts: totalOperations.stats.map((initiated, i) => {
        const successCount = totalSuccess.stats[i]?.count
        const failedCount = totalFailed.stats[i]?.count
        return Math.max(0, initiated.count - successCount - failedCount)
      }),
      amounts: totalOperations.stats.map((initiated, i) => {
        const successAmount = totalSuccess.stats[i]?.amount
        const failedAmount = totalFailed.stats[i]?.amount
        return Math.max(0, initiated.amount - successAmount - failedAmount)
      }),
    },
  ]
}

function updateChartData() {
  if (!props.statsData?.initiated) return
  const dataMap = props.statsData
  const periodKey = selectedPeriod.value // 'year', 'month', 'week'
  const initiated = { stats: dataMap.initiated[periodKey] || [] }
  const succeeded = { stats: dataMap.succeeded[periodKey] || [] }
  const failed = { stats: dataMap.failed[periodKey] || [] }

  // Aligner les catégories de l'axe X avec les vrais titres retournés par l'API
  // (ex: pour l'année, les 12 derniers mois glissants, pas forcément Jan→Déc)
  const categories = initiated.stats.map(el => el.title || '')
  if (categories.length > 0) {
    chartOptions.value = {
      ...chartOptions.value,
      xaxis: { ...chartOptions.value.xaxis, categories },
    }
  }

  setStats(initiated, succeeded, failed)
}

watch([() => props.statsData, selectedPeriod], () => {
  updateChartData()
}, { deep: true })

onMounted(() => {
  selectPeriod(options[2], true)
  updateChartData()
})
</script>

<style scoped>
.area-chart {
  width: 100%;
}
</style>
